'use client';

import {
  DEVICE_LOCATION_CONTEXT_COOKIE,
  DEVICE_LOCATION_HINT_STORAGE_KEY,
  DEVICE_LOCATION_IP_CACHE_STORAGE_KEY,
  type DeviceLocationCookiePayload,
  type DeviceLocationHint,
  type ResolvedDeviceLocation,
} from '@/lib/deviceLocationShared';

const DEVICE_LOCATION_CACHE_TTL_MS = 30 * 60 * 1000;
const DEVICE_LOCATION_COOKIE_MAX_AGE = 10 * 60;

function isValidLocationHint(value: unknown): value is DeviceLocationHint {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<DeviceLocationHint>;
  return (
    typeof candidate.latitude === 'number' &&
    Number.isFinite(candidate.latitude) &&
    typeof candidate.longitude === 'number' &&
    Number.isFinite(candidate.longitude) &&
    (candidate.accuracy === null ||
      typeof candidate.accuracy === 'number' ||
      typeof candidate.accuracy === 'undefined') &&
    typeof candidate.capturedAt === 'string'
  );
}

function readCachedLocationHint() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(DEVICE_LOCATION_HINT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!isValidLocationHint(parsed)) return null;

    if (Date.now() - new Date(parsed.capturedAt).getTime() > DEVICE_LOCATION_CACHE_TTL_MS) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function cacheLocationHint(value: DeviceLocationHint) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(DEVICE_LOCATION_HINT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures.
  }
}

function isValidResolvedDeviceLocation(value: unknown): value is ResolvedDeviceLocation {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<ResolvedDeviceLocation>;
  return (
    (candidate.city == null || typeof candidate.city === 'string') &&
    (candidate.region == null || typeof candidate.region === 'string') &&
    (candidate.country == null || typeof candidate.country === 'string') &&
    (candidate.displayName == null || typeof candidate.displayName === 'string') &&
    (candidate.capturedAt == null || typeof candidate.capturedAt === 'string') &&
    (candidate.accuracyMeters == null || typeof candidate.accuracyMeters === 'number') &&
    (candidate.source === 'gps' || candidate.source === 'ip') &&
    (candidate.precision === 'precise' || candidate.precision === 'approximate')
  );
}

function readCachedIpLocation() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(DEVICE_LOCATION_IP_CACHE_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!isValidResolvedDeviceLocation(parsed)) return null;

    if (!parsed.capturedAt) return null;
    if (Date.now() - new Date(parsed.capturedAt).getTime() > DEVICE_LOCATION_CACHE_TTL_MS) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function cacheIpLocation(value: ResolvedDeviceLocation) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(DEVICE_LOCATION_IP_CACHE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures.
  }
}

function getSecureCookieSuffix() {
  if (typeof window === 'undefined') return '';
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

export function getCurrentTimeZone() {
  if (typeof Intl === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function writeDeviceLocationContextCookie(payload: DeviceLocationCookiePayload) {
  if (typeof document === 'undefined') return;

  document.cookie = `${DEVICE_LOCATION_CONTEXT_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}; Path=/; Max-Age=${DEVICE_LOCATION_COOKIE_MAX_AGE}; SameSite=Lax${getSecureCookieSuffix()}`;
}

export function clearDeviceLocationContextCookie() {
  if (typeof document === 'undefined') return;

  document.cookie = `${DEVICE_LOCATION_CONTEXT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${getSecureCookieSuffix()}`;
}

export async function collectDeviceLocationHint(options?: {
  promptIfNeeded?: boolean;
}): Promise<DeviceLocationHint | null> {
  const cachedHint = readCachedLocationHint();
  if (cachedHint) {
    return cachedHint;
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  if (!('geolocation' in navigator)) {
    return null;
  }

  try {
    let shouldRequestGeolocation = Boolean(options?.promptIfNeeded);

    if ('permissions' in navigator) {
      const permissions = navigator.permissions as Permissions & {
        query: (permissionDesc: PermissionDescriptor) => Promise<PermissionStatus>;
      };
      const permissionStatus = await permissions.query({ name: 'geolocation' as PermissionName });

      if (permissionStatus.state === 'denied') {
        return null;
      }

      if (permissionStatus.state === 'granted') {
        shouldRequestGeolocation = true;
      }
    }

    if (!shouldRequestGeolocation) {
      return null;
    }

    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: DEVICE_LOCATION_CACHE_TTL_MS,
      });
    });

    const nextHint: DeviceLocationHint = {
      latitude: Number(position.coords.latitude.toFixed(6)),
      longitude: Number(position.coords.longitude.toFixed(6)),
      accuracy: Number.isFinite(position.coords.accuracy)
        ? Math.round(position.coords.accuracy)
        : null,
      capturedAt: new Date().toISOString(),
    };

    cacheLocationHint(nextHint);
    return nextHint;
  } catch {
    return null;
  }
}

export async function collectClientDetectedLocation(): Promise<ResolvedDeviceLocation | null> {
  const cachedLocation = readCachedIpLocation();
  if (cachedLocation) {
    return cachedLocation;
  }

  if (typeof window === 'undefined') return null;

  try {
    const response = await fetch('https://ipwho.is/', {
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.success) {
      return null;
    }

    const city = String(data.city || '').trim() || null;
    const region = String(data.region || '').trim() || null;
    const country = String(data.country || '').trim() || null;
    const displayName = city && region ? `${city}, ${region}` : city || region || country;

    if (!displayName) {
      return null;
    }

    const resolvedLocation: ResolvedDeviceLocation = {
      city,
      region,
      country,
      displayName,
      source: 'ip',
      precision: 'approximate',
      accuracyMeters: null,
      capturedAt: new Date().toISOString(),
    };

    cacheIpLocation(resolvedLocation);
    return resolvedLocation;
  } catch {
    return null;
  }
}

export async function collectDeviceLocationContext() {
  const timeZone = getCurrentTimeZone();
  const locationHint = await collectDeviceLocationHint();
  const clientLocation = locationHint ? null : await collectClientDetectedLocation();

  return {
    timeZone,
    locationHint,
    clientLocation,
  };
}

export async function collectDeviceLocationContextWithPrompt() {
  const timeZone = getCurrentTimeZone();
  const locationHint = await collectDeviceLocationHint({ promptIfNeeded: true });
  const clientLocation = locationHint ? null : await collectClientDetectedLocation();

  return {
    timeZone,
    locationHint,
    clientLocation,
  };
}